import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, eventOptions, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import type { Banner } from '../../../app/package/base/cms/package/cms-domain';
import { STORAGE_KEY_IS_VIDEO_PAUSED } from '../../../constants';
import { SubHelper } from '../../base/package/patterns/package/store';
import { localStorage_getOrNull } from '../../base/package/util/localStorage';
import { idleTick } from '../../base/package/util/render';
import style from './ui-slider.css?inline';
import '../ui-banner/ui-banner';
import '../ui-slider-controls/ui-slider-controls';

const CName = 'ui-slider';
const DEFAULT_SLIDER_INTERVAL = 5;

const observerHosts = new WeakMap<IntersectionObserver, WeakRef<UISlider>>();

function handleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
  const ref = observerHosts.get(observer);
  const host = ref?.deref();
  if (!host) {
    observer.disconnect();
    return;
  }
  host._handleIntersection(entries);
}

@customElement(CName)
export class UISlider extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Boolean }) loggedIn?: boolean;
  @property({ type: Boolean }) isDesktop = false;
  @property({ type: Array }) banners: Banner[] = [];

  @state() private _currentIndex = 0;
  @state() private _previousIndex = this.banners.length - 1; // last one by default
  @state() private _interval: number;
  @state() private _videoInterval: number;
  @state() private _animationDuration = DEFAULT_SLIDER_INTERVAL;
  @state() private _isSwiping = false;
  @state() private _initSwipePosition?: number;
  @state() private _isInitialSlideActive = true;
  @state() private _isSlidingBack = false;
  @state() private _isLoading = true;
  @state() private _isPaused = false;
  @state() private _autoAdvanceOnVideoEnd = false;
  @state() private _isInterSecting = true;
  @state() private _isVideoSlide = false;

  @query('#canvas') private readonly _canvas: HTMLCanvasElement | undefined;
  @query('#banner-container') private readonly _bannerContainer?: HTMLElement;

  private _observer: IntersectionObserver | null = null;

  private readonly _subHelper = new SubHelper();
  private readonly _swipeSensitivity = 5;
  private _timeoutStart: number | null = null;

  private readonly _onVideoEnded = () => {
    if (this.isConnected) this._nextAction(true);
  };

  private readonly _onSlideTypeUpdated = (e: CustomEvent<boolean>) => {
    this._isVideoSlide = e.detail;
  };

  private readonly _onSrcLoaded = () => {
    this._isLoading = false;
    if (this.isConnected) this._nextAction();
  };

  /** @internal Called by module-level IntersectionObserver callback. */
  _handleIntersection(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (this._isLoading) return;

      this._isInterSecting = entry.isIntersecting;

      if (!entry.isIntersecting) {
        this._stopAll();
      } else {
        this._playAll();
      }
    }
  }

  // Lit does not guard performUpdate with isConnected — prevent
  // zombie render cycles on detached elements (timer callbacks,
  // late-resolving promises, bubbling events from shadow children).
  protected override scheduleUpdate(): undefined | Promise<unknown> {
    if (!this.isConnected) return;
    return super.scheduleUpdate() as undefined | Promise<unknown>;
  }

  connectedCallback() {
    super.connectedCallback();

    this._subHelper.addSub(
      window.$app.visibilityStore,
      (hidden) => {
        if (this._isLoading) return;
        if (hidden) {
          this._stopAll();
        } else if (this._isInterSecting) {
          this._playAll();
        }
      },
      true,
    );

    idleTick().then(() => {
      this._initIntersectionObserver();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this._clearInterval();
    this._subHelper.unsubscribeAll();

    if (this._observer) {
      this._observer.disconnect();
      observerHosts.delete(this._observer);
      this._observer = null;
    }
  }

  render() {
    const classList = classMap({
      'ui-slider': true,
      'ui-slider--logged-out': this.loggedIn === false && !this.isDesktop,
      'ui-slider--desktop': this.isDesktop,
    });

    return html`
      <div class="${classList}" id="banner-container">
        <canvas id="canvas" class="ui-slider__canvas"></canvas>
        ${this._renderSlider()}
      </div>
      ${this._renderControls()}
    `;
  }

  private _renderControls() {
    return html`<ui-slider-controls
      .banners=${this.banners}
      .allowAnimation=${!this._isLoading && !this._isPaused}
      .currentIndex=${this._currentIndex}
      .animationDuration=${this._animationDuration}
      .buttonClickCb=${(key: number) => this._handleManualNavigation(key)}
    ></ui-slider-controls>`;
  }

  private _renderSlider() {
    return html`<div class="ui-slider__slides">
      ${
        this.banners
          ? repeat(
              this.banners,
              (_, key) => key,
              (banner, key) => {
                const isSingleSlide = this.banners.length === 1;
                const isActive = key === this._currentIndex;
                const isNext = key === (this._currentIndex + 1) % this.banners.length && !isSingleSlide;
                const isPrev = key === this._previousIndex;
                const slideClasses = classMap({
                  'ui-slider__slide': true,
                  'ui-slider__slide--active': isActive,
                  'ui-slider__slide--previous': isPrev,
                  'ui-slider__slide--slide-back': this._isSlidingBack && isActive,
                  'ui-slider__slide--slide-forward': !this._isSlidingBack && isActive && !this._isInitialSlideActive,
                });

                return html`
                  <div class=${slideClasses}>
                    <ui-banner
                      .isInitial=${key === 0 ? this._isInitialSlideActive : false}
                      .banner=${banner}
                      .isDesktop=${this.isDesktop}
                      .isSingleSlide=${isSingleSlide}
                      .isActive=${isActive}
                      .isNext=${isNext}
                      .position=${key}
                      .loggedIn=${this.loggedIn}
                      @touchstart=${this._handleTouchStart}
                      @touchmove=${this._handleTouch}
                      @touchend=${this._handleTouchEnd}
                      @draw-video-canvas=${this._drawCanvas}
                      @video-ended=${this._onVideoEnded}
                      @banner-video-paused=${this._onVideoPaused}
                      @update-animation-speed=${this._updateAnimationSpeed}
                      @slide-type-updated=${this._onSlideTypeUpdated}
                      @src-loaded=${this._onSrcLoaded}
                    ></ui-banner>
                  </div>
                `;
              },
            )
          : nothing
      }
    </div>`;
  }

  private _initIntersectionObserver() {
    this._observer = new IntersectionObserver(handleIntersection);
    observerHosts.set(this._observer, new WeakRef(this));
    if (this._bannerContainer) this._observer.observe(this._bannerContainer);
  }

  private _drawImageCanvas(ctx: CanvasRenderingContext2D | null, img: string) {
    const canvasImage = new Image();
    canvasImage.src = img;
    canvasImage.onload = () => {
      ctx!.drawImage(canvasImage, 0, 0);
    };
  }

  private _drawVideoCanvas(ctx: CanvasRenderingContext2D | null, e: CustomEvent) {
    // refresh every tenth frame
    const refreshRate = (1_000 / 60) * 10;

    this._videoInterval = window.setInterval(() => {
      ctx!.drawImage(e.detail, 0, 0, e.detail.offsetWidth, e.detail.offsetHeight);
    }, refreshRate);
  }

  private _drawCanvas(videoSource?: CustomEvent) {
    if (this.isDesktop || !this._canvas) return;
    clearInterval(this._videoInterval);
    const ctx = this._canvas.getContext('2d');

    if (this._isVideoSlide) {
      if (videoSource) {
        this._drawVideoCanvas(ctx, videoSource);
      }
      return;
    }

    const canvasImg = this.banners[this._currentIndex]?.mobile_image;
    if (canvasImg === undefined) {
      return;
    }
    this._drawImageCanvas(ctx, canvasImg);
  }

  private _clearInterval() {
    clearTimeout(this._interval);
    clearInterval(this._videoInterval);
  }

  private _handleManualNavigation(key: number) {
    this._animationDuration = this.banners[key]?.duration || DEFAULT_SLIDER_INTERVAL;

    if (key !== this._currentIndex) {
      this._isSlidingBack = key < this._currentIndex;
      this._triggerEvent(key);
    }
  }

  private _getNextSlideIndex(index: number) {
    const lastIndex = this.banners.length - 1;
    return index + 1 > lastIndex ? 0 : index + 1;
  }

  private _getPreviousSlideIndex(index: number) {
    const lastIndex = this.banners.length - 1;
    return index === 0 ? lastIndex : index - 1;
  }

  private _onVideoPaused() {
    if (this._interval) clearTimeout(this._interval);
    if (this._timeoutStart) {
      const elapsed = (Date.now() - this._timeoutStart) / 1000;
      const remainingTime = Math.max(this._animationDuration - elapsed, 0.01);
      this._interval = window.setTimeout(() => {
        if (!this.isConnected) return;
        const nextSlideIdx = this._getNextSlideIndex(this._currentIndex);
        this._triggerEvent(nextSlideIdx);
      }, remainingTime * 1000);
    }
  }

  private _animateSlider() {
    const currentBanner = this.banners[this._currentIndex];

    if (this.banners.length === 1 || !currentBanner) {
      return;
    }

    if (this._interval) {
      this._clearInterval();
    }

    this._animationDuration = this.banners[this._currentIndex]?.duration || DEFAULT_SLIDER_INTERVAL;
    this._timeoutStart = Date.now();
    let isVideoSlide: boolean;
    if (this.isDesktop) {
      isVideoSlide = !!currentBanner.desktop_vid;
    } else if (this.loggedIn) {
      isVideoSlide = !!currentBanner.mobile_vid_small || !!currentBanner.mobile_vid;
    } else {
      isVideoSlide = !!currentBanner.mobile_vid;
    }
    const isVideoPaused = localStorage_getOrNull(STORAGE_KEY_IS_VIDEO_PAUSED) === true;
    this._autoAdvanceOnVideoEnd = false;
    if (isVideoSlide && !currentBanner.duration && !isVideoPaused) {
      this._autoAdvanceOnVideoEnd = true;
      // For video slides (without duration set), skip setting a timeout.
      // The next slide will trigger once the video completes.
      return;
    }

    this._interval = window.setTimeout(() => {
      if (!this.isConnected) return;
      const nextSlideIdx = this._getNextSlideIndex(this._currentIndex);
      this._triggerEvent(nextSlideIdx);
    }, this._animationDuration * 1000);
  }

  private _updateAnimationSpeed(e: CustomEvent) {
    // Update only if auto advance on video end is enabled and the duration has changed
    if (this._autoAdvanceOnVideoEnd && e.detail && !Number.isNaN(e.detail) && this._animationDuration !== e.detail) {
      this._animationDuration = e.detail;
    }
  }

  @eventOptions({ passive: true })
  private _handleTouch(e: TouchEvent) {
    if (this._isSwiping || this.banners.length === 1) {
      return;
    }

    const endPosition = e.touches[0]?.pageX;

    if (this._initSwipePosition === undefined || endPosition === undefined) {
      return;
    }

    const deltaX = Math.floor(this._initSwipePosition - endPosition);
    const isRightSwipe = deltaX > this._swipeSensitivity;
    const isLeftSwipe = deltaX < -this._swipeSensitivity;

    if (isRightSwipe || isLeftSwipe) {
      this._isSwiping = true;
      let nextIndex: number;

      if (isRightSwipe) {
        this._isSlidingBack = false;
        nextIndex = this._getNextSlideIndex(this._currentIndex);
      } else {
        this._isSlidingBack = true;
        nextIndex = this._getPreviousSlideIndex(this._currentIndex);
      }

      this._triggerEvent(nextIndex);
    }
  }

  private _triggerEvent(index: number) {
    const prevIndex = this._currentIndex;
    this._currentIndex = index;
    this._drawCanvas();
    this._animateSlider();
    this._previousIndex = prevIndex;
    this._isInitialSlideActive = false;
  }

  @eventOptions({ passive: true })
  private _handleTouchStart(e: TouchEvent) {
    this._initSwipePosition = e.touches[0]?.pageX;
  }

  @eventOptions({ passive: true })
  private _handleTouchEnd() {
    this._isSwiping = false;
  }

  private _nextAction(triggeredByVideoEnding = false) {
    if (this.banners?.length) {
      this._drawCanvas();

      if (!triggeredByVideoEnding) {
        this._animateSlider();
        return;
      }

      if (this._autoAdvanceOnVideoEnd) {
        this._triggerEvent(this._getNextSlideIndex(this._currentIndex));
      }
    }
  }

  private _playAll() {
    this._nextAction();
    this._isPaused = false;
  }

  private _stopAll() {
    this._clearInterval();
    this._isPaused = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UISlider;
  }
}
